import { clsx } from 'clsx';
import { Check } from 'lucide-react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import type { ChangeEvent, FocusEvent, Ref } from 'react';
import React from 'react';
import { InjuryRadio } from './injury-radio';
import { LeadInput } from '@jlg-website/shared';
import { ArrowRight } from '@/components/pages/icons';
import Cta from '@/components/pages/cta';
import type { ContactProps, MessageComponent } from './type';

export type FormServerProps<TFormValues extends LeadInput = LeadInput> = ContactProps & {
  isDisabled?: boolean;
  isSucceeded?: boolean;
  isFailed?: boolean;
  isHidden?: boolean;

  phoneNumberValue?: string;
  caseTypeValue?: string;
  wereYouInjuredValue?: string;

  register?: UseFormRegister<LeadInput>;
  errors?: FieldErrors<LeadInput>;
  onSubmit?: (e?: React.BaseSyntheticEvent) => void | Promise<void>;

  onCaseTypeChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onWereYouInjuredChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onZipCodeBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onPhoneNumberBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onPhoneNumberChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  hideForm?: () => void;

  SuccessMessage?: MessageComponent;
  FailureMessage?: MessageComponent;

  mode?: 'server' | 'client';

  submitRef?: Ref<HTMLElement>;
  onClickSticky?: () => void;

  fieldsPrefix: string;
  hideInjuryQuestions?: boolean;
};

export function FormServer<TFormValues extends LeadInput>({
  mode = 'server',
  variant = 'dark',
  submitVariation = 'default',
  consent,
  isDisabled,
  isSucceeded,
  isFailed,
  isHidden,
  errors,
  onSubmit,
  register,
  onCaseTypeChange,
  onWereYouInjuredChange,
  onZipCodeBlur,
  onPhoneNumberBlur,
  onPhoneNumberChange,
  SuccessMessage,
  FailureMessage,
  phoneNumberValue,
  className,
  preppendForm,
  appendForm,
  appendMessage,
  sticky,
  submitRef,
  onClickSticky,
  fieldsPrefix,
  hideInjuryQuestions,
}: FormServerProps<TFormValues>) {
  const inputClasses =
    'ring-1 ring-black/20 px-4 py-3 rounded-lg bg-white text-brand-default focus:outline-none w-full';

  const errorClasses = {
    light:
      'ring-brand-accent-error-dark! text-brand-accent-error-dark! placeholder:text-brand-accent-error/50!',
    dark: 'ring-brand-accent-error! text-brand-accent-error! placeholder:text-brand-accent-error/50!',
  };

  const disabled = mode === 'server' || isDisabled;

  const textClasses = {
    dark: 'text-brand-default',
    light: 'text-white [&_a]:hover:text-brand-button',
  };
  const labelButtonBase =
    'cursor-pointer flex items-center gap-1 ring-1 rounded-full text-center justify-center px-3 py-2 text-sm tracking-[0.21px] font-semibold flex-nowrap whitespace-nowrap leading-[100%] [&_svg]:w-4 h-8 [&_svg]:hidden [&_svg]:@lg:block';
  const labelButtonClasses = {
    dark: 'ring-brand-accent text-brand-accent hover:bg-black/10 hover:text-brand-button has-checked:bg-brand-button has-checked:text-white has-checked:ring-brand-button',
    light:
      'ring-white text-white hover:bg-black/10 has-checked:bg-brand-button has-checked:text-white has-checked:ring-brand-button',
  };

  if (isHidden) {
    return null;
  }

  if (isSucceeded && SuccessMessage) {
    return <SuccessMessage variant={variant} className={className} appendMessage={appendMessage} />;
  }

  if (isFailed && FailureMessage) {
    return <FailureMessage variant={variant} className={className} appendMessage={appendMessage} />;
  }

  return (
    <>
      {preppendForm}
      <form
        onSubmit={mode === 'client' ? onSubmit : undefined}
        className={clsx(
          'flex flex-col',
          mode === 'server' && 'pointer-events-none opacity-60',
          className
        )}
      >
        <div className="grid grid-cols-2 gap-6 mb-4">
          <div className="col-span-2">
            <div className={`text-base mb-1.5 ${textClasses[variant]}`}>Tell us about yourself</div>
            <div className="grid gril-cols-2 gap-4">
              <label htmlFor={`${fieldsPrefix}-lead.contact.first_name`} className="sr-only">
                First name
              </label>
              <input
                id={`${fieldsPrefix}-lead.contact.first_name`}
                placeholder="First name"
                type="text"
                {...(mode === 'client' ? register?.('lead.contact.first_name') : {})}
                className={clsx(
                  inputClasses,
                  errors?.lead?.contact?.first_name && errorClasses[variant]
                )}
                title={`First name ${errors?.lead?.contact?.first_name?.message || ''}`}
                disabled={disabled}
              />

              <label htmlFor={`${fieldsPrefix}-lead.contact.last_name`} className="sr-only">
                Last name
              </label>
              <input
                id={`${fieldsPrefix}-lead.contact.last_name`}
                {...(mode === 'client' ? register?.('lead.contact.last_name') : {})}
                className={clsx(
                  inputClasses,
                  errors?.lead?.contact?.last_name && errorClasses[variant]
                )}
                placeholder="Last name"
                title={`Last name ${errors?.lead?.contact?.last_name?.message || ''}`}
                type="text"
                disabled={disabled}
              />

              <label htmlFor={`${fieldsPrefix}-lead.contact.phone`} className="sr-only">
                Phone number
              </label>
              <input
                id={`${fieldsPrefix}-lead.contact.phone`}
                className={clsx(
                  inputClasses,
                  errors?.lead?.contact?.phone && errorClasses[variant]
                )}
                placeholder="Phone number"
                title={`Phone number ${errors?.lead?.contact?.phone?.message || ''}`}
                type="text"
                value={phoneNumberValue}
                onChange={mode === 'client' ? onPhoneNumberChange : undefined}
                onBlur={mode === 'client' ? onPhoneNumberBlur : undefined}
                disabled={disabled}
              />

              <label htmlFor={`${fieldsPrefix}-lead.contact.zip_code}`} className="sr-only">
                ZIP code
              </label>
              <input
                id={`${fieldsPrefix}-lead.contact.zip_code`}
                {...(mode === 'client' ? register?.('lead.contact.zip_code') : {})}
                className={clsx(
                  inputClasses,
                  errors?.lead?.contact?.zip_code && errorClasses[variant]
                )}
                onBlur={mode === 'client' ? onZipCodeBlur : undefined}
                placeholder="ZIP code"
                title={`ZIP code ${errors?.lead?.contact?.zip_code?.message || ''}`}
                type="text"
                disabled={disabled}
              />

              <label htmlFor={`${fieldsPrefix}-lead.contact.email`} className="sr-only">
                Email
              </label>
              <input
                id={`${fieldsPrefix}-lead.contact.email`}
                {...(mode === 'client' ? register?.('lead.contact.email') : {})}
                className={clsx(
                  inputClasses,
                  'col-span-2',
                  errors?.lead?.contact?.email && errorClasses[variant]
                )}
                placeholder="Email"
                type="email"
                title={`Email ${errors?.lead?.contact?.email?.message || ''}`}
                disabled={disabled}
              />
            </div>
          </div>

          {!hideInjuryQuestions && (
            <>
              <div className="col-span-2">
                <div
                  className={clsx(
                    'text-base mb-1.5',
                    textClasses[variant],
                    errors?.lead?.answers?.[1] && errorClasses[variant]
                  )}
                >
                  What do you need help with?
                </div>
                <InjuryRadio
                  labelButtonBase={labelButtonBase}
                  labelButtonClass={labelButtonClasses[variant]}
                  errors={errors}
                  disabled={disabled}
                  errorClasses={errorClasses[variant]}
                  onChange={mode === 'client' ? onCaseTypeChange : undefined}
                />
              </div>

              <div className="col-span-2">
                <div
                  className={clsx(
                    'text-base mb-1.5',
                    textClasses[variant],
                    errors?.lead?.answers?.[2] && errorClasses[variant]
                  )}
                >
                  Were you injured?
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <label
                    className={clsx(
                      'justify-center',
                      labelButtonBase,
                      labelButtonClasses[variant],
                      errors?.lead?.answers?.[2] && errorClasses[variant]
                    )}
                  >
                    <span>Yes</span>
                    <input
                      name="injured"
                      type="radio"
                      value="8"
                      className="sr-only"
                      onChange={onWereYouInjuredChange}
                      disabled={disabled}
                    />
                  </label>
                  <label
                    className={clsx(
                      'justify-center',
                      labelButtonBase,
                      labelButtonClasses[variant],
                      errors?.lead?.answers?.[2] && errorClasses[variant]
                    )}
                  >
                    <span>No</span>
                    <input
                      name="injured"
                      type="radio"
                      value="9"
                      className="sr-only"
                      onChange={onWereYouInjuredChange}
                      disabled={disabled}
                    />
                  </label>
                </div>
              </div>
            </>
          )}

          <label htmlFor={`${fieldsPrefix}-lead.contact.details`} className="sr-only">
            Tell us what happened
          </label>
          <textarea
            id={`${fieldsPrefix}-lead.contact.details`}
            {...(mode === 'client' ? register?.('lead.contact.details') : {})}
            placeholder="Tell us what happened"
            className={clsx(
              inputClasses,
              'col-span-2',
              errors?.lead?.contact?.details && errorClasses[variant]
            )}
            title={`Tell us what happened ${errors?.lead?.contact?.details || ''}`}
            disabled={disabled}
          />
        </div>

        <label className="group flex items-start gap-2 col-span-2 cursor-pointer mb-4">
          <input
            type="checkbox"
            className="sr-only"
            {...(mode === 'client' ? register?.('lead.contact.consent') : {})}
          />

          <div
            className={clsx(
              'border border-white bg-white group-has-checked:bg-brand-button min-w-5.5 h-5.5 flex items-center justify-center rounded-lg transition-colors',
              errors?.lead?.contact?.consent && errorClasses[variant]
            )}
          >
            <Check className="size-4 text-white opacity-0 group-has-checked:opacity-100 transition-opacity" />
          </div>

          <div
            className={clsx(
              'grow text-[12px] font-medium leading-normal',
              textClasses[variant],
              errors?.lead?.contact?.consent && errorClasses[variant]
            )}
            dangerouslySetInnerHTML={{ __html: consent }}
          />
        </label>

        <Cta
          disabled={disabled}
          variation={submitVariation === 'flat' ? 'flat' : 'shadow'}
          text="Start free review"
          icon={<ArrowRight />}
          type={sticky ? 'button' : 'submit'}
          onClick={sticky ? onClickSticky : undefined}
          className={clsx('col-span-2', sticky && 'sticky top-0 bottom-4')}
        />
        <div ref={submitRef as React.Ref<HTMLDivElement>} />
      </form>
      {appendForm}
    </>
  );
}
